import React, { useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { StyledCitationNumber, StyledCitationValueViewerWrapper } from './CitationValueViewer.styles';
import CitationPopover from '../../../../../components/Popovers/citation-popover/CitationPopover';
import FastValueViewer from '../../../../../components/ScalarSpreadsheet/utilities/FastValueViewer';
import { useDocumentFootnotes } from '../../../../../context/DocumentFootnotesContext';
import { CitationReferenceData } from '../../../../../services/reference-generator/types';
import { CapTableCellWithReferences } from '../captable-table/CaptableTable.props';
import { useCitationContext } from '../captable-table/CitationContext';

interface CitationValueViewerProps {
  value: string | number;
  citationReferences?: CitationReferenceData[];
  citationNumber?: number;
  citation?: CitationReferenceData;
  originalValueViewer?: React.ComponentType<any>;
  cell: CapTableCellWithReferences;
  [key: string]: any; // gross
}

const CitationValueViewer = ({
  value,
  citationReferences,
  citationNumber,
  originalValueViewer: OriginalValueViewer,
  citation,
  ...restProps
}: CitationValueViewerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const { setActiveCitation, activeCitation } = useCitationContext();
  const { getSourceNumber } = useDocumentFootnotes();

  // Extract all citation information from tokenReferences
  const citationData = useMemo(() => {
    if (!citationReferences || citationReferences.length === 0) {
      return { notes: [], plainTexts: [] };
    }

    const notes: string[] = [];
    const plainTexts: string[] = [];

    citationReferences.forEach(citation => {
      citation.tokenReferences.forEach(tokenRef => {
        if (tokenRef.plain_text) {
          plainTexts.push(tokenRef.plain_text);
        }
        if (tokenRef.notes && tokenRef.notes.length > 0) {
          notes.push(...tokenRef.notes);
        }
      });
    });

    return { notes, plainTexts };
  }, [citationReferences]);

  const citationSourceNumbers = useMemo(() => {
    if (!citationReferences || citationReferences.length === 0) {
      return [];
    }

    const sourceNumbers: number[] = [];
    citationReferences.forEach(citation => {
      const sourceNumber = getSourceNumber(citation.document_reference);
      if (sourceNumber !== undefined && !sourceNumbers.includes(sourceNumber)) {
        sourceNumbers.push(sourceNumber);
      }
    });

    return sourceNumbers.sort((a, b) => a - b);
  }, [citationReferences, getSourceNumber]);

  const displayCitationNumber = useMemo(() => {
    if (citationNumber !== undefined) {
      return citationNumber;
    }

    if (citationSourceNumbers.length > 0) {
      return citationSourceNumbers[0];
    }

    const contentHash = citationData.notes.join('|') + citationData.plainTexts.join('|');
    if (!contentHash) return 1;

    // fallback to generate a stable number from the citation content
    // eslint-disable-next-line no-bitwise
    const hash = contentHash.split('').reduce((acc, char) => (acc << 5) - acc + char.charCodeAt(0), 0);

    return (Math.abs(hash) % 999) + 1;
  }, [citationNumber, citationSourceNumbers, citationData]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const titleCell = useMemo(() => {
    const columnId = restProps.cell.columnLegend;
    const titleCellForColumn = restProps.cell.sheet?.cells?.[`${columnId}1`];
    return titleCellForColumn?.value ? (titleCellForColumn.value as string) : 'Unknown...';
  }, [restProps.cell]);

  const handleCitationClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!citationReferences || citationReferences.length === 0) {
      return;
    }

    // If popover is already open, close it
    if (open) {
      handleClose();
      return;
    }

    setActiveCitation(citationReferences[0]);
    handleClick(e);
  };

  const handleCitationMouseDown = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleCitationDelete = (citationId: number) => {
    const newCitationReferences = restProps.cell.citationReferences?.filter(citation => citation.id !== citationId);

    // eslint-disable-next-line no-param-reassign
    restProps.cell.citationReferences = newCitationReferences;

    // eslint-disable-next-line no-param-reassign
    restProps.cell.valueViewer = null;

    // eslint-disable-next-line no-param-reassign
    restProps.cell.dispatchEvent();
  };

  if (!citationReferences || citationReferences.length === 0) {
    // If no citations, render original viewer or plain text
    if (OriginalValueViewer) {
      return <OriginalValueViewer value={value} {...restProps} />;
    }
    return <FastValueViewer value={value} {...restProps} />;
  }

  return (
    <StyledCitationValueViewerWrapper onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      {/* Small blue box with citation number(s) - always visible */}
      {(isHovered || activeCitation?.id === citationReferences[0].id) && (
        <StyledCitationNumber
          onClick={handleCitationClick}
          onMouseDown={handleCitationMouseDown}
          sx={{
            maxWidth: citationSourceNumbers.length > 1 ? '40px' : '24px',
            backgroundColor: activeCitation?.id === citationReferences[0].id ? 'primary.main' : 'primary.light',
            color: activeCitation?.id === citationReferences[0].id ? 'primary.contrastText' : 'primary.dark',
            cursor: 'pointer',
            zIndex: 10,
            position: 'relative',
          }}>
          {citationSourceNumbers.length > 1 ? citationSourceNumbers.join(',') : displayCitationNumber}
        </StyledCitationNumber>
      )}

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
        {OriginalValueViewer ? (
          <OriginalValueViewer value={value} {...restProps} />
        ) : (
          <FastValueViewer className="text-cell" value={value} {...restProps} />
        )}
      </Box>

      <CitationPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        citation={citationReferences[0]}
        columnarIdentifier={titleCell}
        onDelete={handleCitationDelete}
      />
    </StyledCitationValueViewerWrapper>
  );
};

export default CitationValueViewer;
